import React, { useState, useEffect, useRef } from "react";
import { formatPrice, generateID, isValidateEmail } from '../helpers/CommonHelpers';
import { useNavigate } from "react-router-dom";
import { createDBSession, updateDBSession, deleteDBSession, PersonList, EmptyCartHTML, AllreadyHaveAccountHTML, ConfirmPopup, CartFeaturedEventItem, CartFeaturedPlanItem, calculateGrandTotal, OrderSummary } from "../components/ReusableComponents";
import { cartToItems, getCurrency, pushEcommEvent } from "../utils/ga4";

const Cart = () => {
    const im_custom = window.im_ecom || { events: [], plans: [], event_price: { extra_training_tickets_price: 0, price: 0 }, user: null, is_logged_in: false };
    const navigate = useNavigate();
    const plans = (im_custom?.plans && Array.isArray(im_custom.plans)) ? im_custom.plans : [];
    const events = (im_custom?.events && Array.isArray(im_custom.events)) ? im_custom.events : [];


    //  Modal visibility
    const [showModal, setShowModal] = useState(false)
    const [grandTotal, setGrandTotal] = useState(0);
    const [highlightAttendeeError, setHighlightAttendeeError] = useState(false);

    const [modalData, setModalData] = useState({
        title: "",
        message: "",
        action: null,
    });

    const userAgreedRef = useRef(null);
    const attendeeAgreedRef = useRef(null);

    // Track if cart has been initialized (loaded from localStorage or has content)
    const isInitializedRef = useRef(false);
    /* Helper: get purchaser's first/last name, splitting `name` if needed */
    const getPurchaserFirstName = () => {
        if (!im_custom.user) return "";
        if (im_custom.user.first_name) return im_custom.user.first_name;
        if (im_custom.user.name) return im_custom.user.name.split(' ')[0] || "";
        return "";
    };
    const getPurchaserLastName = () => {
        if (!im_custom.user) return "";
        if (im_custom.user.last_name) return im_custom.user.last_name;
        if (im_custom.user.name) return im_custom.user.name.split(' ').slice(1).join(' ') || "";
        return "";
    };
    const getPurchaserEmail = () => (im_custom.user?.email || "");

    /* New person template */
    const newPerson = (price, isDefault = "1", type = "user",) => ({
        'id': generateID(type === "user" ? "user_" : "attendee_"),
        'firstName': "",
        'lastName': "",
        'email': "",
        'confirmEmail': "",
        'label': "Additional " + (type === "user" ? " User" : " Attendee"),
        'price': price,
        'isDefault': isDefault,
        'leaveSeatUnassigned': type === "attendee" ? "0" : "1",  // attendee unchecked
        'openUserForm': type === "attendee" ? "1" : 0,        // attendee form open
    });

    /* User cart state */
    const [userCart, setUserCart] = useState({
        users: [],
        attendees: [],
        activePlan: null,
        activeEvent: null,
        isAgreed: "0",
        sessionID: null,
        isUserAgreed: "0",
        highlightUserAgreed: false,
        isAttendeeAgreed: "0",
        highlightAttendeeAgreed: false,
        paymentMethod: "credit_card",
    });

    /* Validate person info */
    const validatePerson = (p) => {
        // let errors = {};
        // if (p.firstNameCheck && !p.firstName) errors.firstName = "First name required";
        // if (p.lastNameCheck && !p.lastName) errors.lastName = "Last name required";
        // if (p.emailCheck && !p.email) errors.email = "Email required";
        // if (p.emailCheck && p.email && !isValidateEmail(p.email)) errors.email = "Invalid email address";
        // if (p.confirmEmailCheck && !p.confirmEmail) errors.confirmEmail = "Confirm email required";
        // if (p.confirmEmailCheck && p.email !== p.confirmEmail) errors.confirmEmail = "Emails do not match";
        // return errors;
    };

    /* Load cart from localStorage */
    useEffect(() => {
        const saved = localStorage.getItem("user_cart");
        const savedParse = saved ? JSON.parse(saved) : null;
        if (savedParse) {
            console.log("Loaded saved cart:", savedParse);

            //  attendee/user forms open if not fully filled,
            const isFilled = (p) => p.firstName && p.lastName && p.email && p.confirmEmail;
            const fixedAttendees = (savedParse.attendees || []).map((a) => ({
                ...a,
                openUserForm: isFilled(a) ? "0" : "1",
            }));
            const fixedUsers = (savedParse.users || []).map((u) => ({
                ...u,
                openUserForm: (u.leaveSeatUnassigned === "1") ? "0" : (isFilled(u) ? "0" : "1"),
            }));

            const fixedCart = { ...savedParse, attendees: fixedAttendees, users: fixedUsers };
            setUserCart(fixedCart);

            // Mark as initialized if cart has content
            if (savedParse.activePlan || savedParse.activeEvent) {
                isInitializedRef.current = true;
            }
        }
    }, []);

    /* If purchaser info becomes available after initial render (async load
       of window.im_ecom.user, or user logs in), backfill the first
       attendee's prefill fields if they're still empty. */
    useEffect(() => {
        if (!im_custom.is_logged_in || !im_custom.user) return;
        setUserCart((p) => {
            if (!p.attendees || p.attendees.length === 0) return p;
            const first = p.attendees[0];
            if (first.firstName || first.lastName || first.email) return p;
            const updatedAttendees = p.attendees.map((a, idx) => {
                if (idx !== 0) return a;
                return {
                    ...a,
                    firstName: getPurchaserFirstName(),
                    lastName: getPurchaserLastName(),
                    email: getPurchaserEmail(),
                    openUserForm: "1",
                    leaveSeatUnassigned: "0",
                };
            });
            return { ...p, attendees: updatedAttendees };
        });
    }, [im_custom.is_logged_in, im_custom.user?.email]);

    /* Create DB session when cart has items but no sessionID */
    useEffect(() => {
        // Only proceed if cart is initialized and has content
        if (!isInitializedRef.current) return;
        if (!userCart.activePlan && !userCart.activeEvent) return;

        // Generate sessionID and create DB session if it doesn't exist
        if (!userCart.sessionID) {
            const newSessionID = generateID("session_");
            const updatedCart = { ...userCart, sessionID: newSessionID };
            setUserCart(updatedCart);
            localStorage.setItem("user_cart", JSON.stringify(updatedCart));

            // Create new DB session
            createDBSession(newSessionID, im_custom.user?.user_id || null, updatedCart);
        }
    }, [userCart.activePlan, userCart.activeEvent, userCart.sessionID]);

    /* Update grand total and save cart to localStorage on userCart change */
    useEffect(() => {
        // console.log("User cart changed:", userCart);
        setGrandTotal(calculateGrandTotal(userCart, im_custom));

        // Mark as initialized when cart gets content
        if (!isInitializedRef.current && (userCart.activePlan || userCart.activeEvent)) {
            isInitializedRef.current = true;
        }

        // Only persist if cart is initialized and has content
        if (!isInitializedRef.current) return;
        // if (!userCart.activePlan && !userCart.activeEvent) return;
         if (!userCart.activePlan && !userCart.activeEvent && userCart.attendees.length === 0 && userCart.users.length === 0) return;

        localStorage.setItem("user_cart", JSON.stringify(userCart));

        // Only update DB session if sessionID exists
        if (userCart.sessionID) {
            const delayDebounce = setTimeout(() => {
                updateDBSession(userCart.sessionID, im_custom.user?.user_id || null, userCart);
            }, 10000);
            return () => clearTimeout(delayDebounce);
        }

    }, [userCart]);

    // GA4: view_cart and add_to_cart on cart changes
    useEffect(() => {
        if (!userCart.activePlan && !userCart.activeEvent) return;
        const currency = getCurrency(im_custom);
        const value = calculateGrandTotal(userCart, im_custom);
        const items = cartToItems(userCart, im_custom);

        pushEcommEvent('view_cart', { currency, value, items });
        pushEcommEvent('add_to_cart', { currency, value, items });
    }, [userCart.activePlan, userCart.activeEvent, userCart.users.length, userCart.attendees.length]);

    /* Change active event */
    const changeActiveEvent = (event) => {
        setUserCart((p) => ({
            ...p,
            activeEvent: event,
            // Reset users and attendees based on new plan),
        }));
    }

    /* Add event to cart */
    const addEventToCart = () => {
        setUserCart((p) => ({
            ...p,
            attendees: [...p.attendees, {
                ...newPerson(im_custom.event_price.price, "1", "attendee"),
                firstName: getPurchaserFirstName(),
                lastName: getPurchaserLastName(),
                email: getPurchaserEmail(),
                confirmEmail: "",    
                leaveSeatUnassigned: "0",
                openUserForm: "1",
            }],
        }));
    }

    /* User management functions */
    const addUser = () => setUserCart((p) => ({
        ...p,
        users: [...p.users, {
            ...newPerson(userCart.activePlan.extra_atlas_user_price, "0", "user"),
            leaveSeatUnassigned: "0",  // unchecked
            openUserForm: "1",          // form open
        }]
    }));
    const removeUser = (id) => setUserCart((p) => ({ ...p, users: p.users.filter((u) => u.id !== id) }));
    const updateUser = (id, updates) => {
        setUserCart((p) => ({
            ...p,
            users: p.users.map((u) => {
                if (u.id !== id) return u;
                const updated = { ...u, ...updates };
                return { ...updated, errors: validatePerson(updated) };
            }),
        }));
    };

    /* Attendee management functions */
    const addAttendee = () => setUserCart((p) => ({
        ...p,
        attendees: [...p.attendees, {
            ...newPerson(im_custom.event_price.extra_training_tickets_price, "0", "attendee"),
            leaveSeatUnassigned: "0",   // unchecked
            openUserForm: "1",           // form open
        }]
    }));
    const removeAttendee = (id) => setUserCart((p) => ({ ...p, attendees: p.attendees.filter((a) => a.id !== id) }));
    const updateAttendee = (id, updates) => {
        setUserCart((p) => ({
            ...p,
            attendees: p.attendees.map((a) => {
                if (a.id !== id) return a;
                const updated = { ...a, ...updates };
                return { ...updated, errors: validatePerson(updated) };
            }),
        }));
    };

    /* Continue to checkout logic */
    const handleContinueToCheckout = () => {
        if (!im_custom.is_logged_in) {
            // then send the purchaser to create an account first.
            localStorage.setItem("user_cart", JSON.stringify(userCart));
            window.location.href = "/user/register?redirect_to=/cart";
            return;
        }

        // Attendee validation — all attendees details details should be filled before checkout
        const unfilledAttendee = userCart.attendees.find(
            (a) => !a.firstName || !a.lastName || !a.email || !a.confirmEmail
        );
        if (unfilledAttendee) {
            updateAttendee(unfilledAttendee.id, { openUserForm: "1" });
            setHighlightAttendeeError(true);
            setTimeout(() => setHighlightAttendeeError(false), 2000);
            const el = document.getElementById(`attendee-${unfilledAttendee.id}`);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
            return;
        }


        if (userCart.users.length > 0 && userCart.isUserAgreed == "0") {
            setUserCart((p) => ({
                ...p,
                highlightUserAgreed: true
            }));
            userAgreedRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "center"
            });
            setTimeout(() => {
                setUserCart((p) => ({
                    ...p,
                    highlightUserAgreed: false
                }));
            }, 2000);
            return;
        }
        if (userCart.attendees.length > 0 && userCart.isAttendeeAgreed == "0") {
            setUserCart((p) => ({
                ...p,
                highlightAttendeeAgreed: true
            }));
            attendeeAgreedRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
            setTimeout(() => {
                setUserCart((p) => ({
                    ...p,
                    highlightAttendeeAgreed: false
                }));
            }, 2000);
            return;
        }
        navigate("/checkout_and_payment");
    };

    /* Remove entire cart */
    const handleRemoveCart = () => {
        if (userCart.activePlan || userCart.activeEvent) {
            const currency = getCurrency(im_custom);
            const value = calculateGrandTotal(userCart, im_custom);
            const items = cartToItems(userCart, im_custom);
            pushEcommEvent('remove_from_cart', { currency, value, items });
        }
        // delete only plan if there is no training tickets but attendees
        if (userCart.activePlan.training_tickets === 0 && userCart.attendees.length > 0) {
            const updatedCart = { ...userCart, activePlan: null, users: [], isUserAgreed: "0", highlightUserAgreed: false };
            setUserCart(updatedCart);
            localStorage.setItem("user_cart", JSON.stringify(updatedCart));
        } else {
            if (userCart.sessionID) {
                deleteDBSession(userCart.sessionID);
            }
            const updatedCart = {
                ...userCart,
                users: [],
                attendees: [],
                activePlan: null,
                activeEvent: null,
                isAgreed: "0",
                sessionID: null,
                isUserAgreed: "0",
                highlightUserAgreed: false,
                isAttendeeAgreed: "0",
                highlightAttendeeAgreed: false,
            };
            setUserCart(updatedCart);
            isInitializedRef.current = false;
            localStorage.removeItem("user_cart");
        }
        setShowModal(false);
    };

    /* Remove event from cart */
    const handleRemoveEvent = (e) => {
        e.preventDefault();
        if (userCart.activeEvent) {
            const currency = getCurrency(im_custom);
            const value = calculateGrandTotal(userCart, im_custom);
            const items = cartToItems(userCart, im_custom);
            pushEcommEvent('remove_from_cart', { currency, value, items });
        }
        const updatedCart = { ...userCart, activeEvent: null, attendees: [] };
        setUserCart(updatedCart);
        localStorage.setItem("user_cart", JSON.stringify(updatedCart));
        setShowModal(false);
    }

    /* If cart is empty, show empty cart message */
    if (userCart.users.length == 0 && userCart.attendees.length == 0) {
        return (<EmptyCartHTML />);
    }

    /* Main cart rendering */
    return (
        <div className="about-wrapper">
            <p className={`i-agree-error-flag ${userCart.highlightUserAgreed ? "shake-text" : ""}`}>Please check this box to allow all members to receive emails from Directions on Microsoft.</p>
            <p className={`i-agree-error-flag ${userCart.highlightAttendeeAgreed ? "shake-text" : ""}`}>Please check this box to allow all training session attendees to receive emails from Directions on Microsoft.</p>
            <p className={`i-agree-error-flag ${highlightAttendeeError ? "shake-text" : ""}`}>Please fill in all attendee details.</p>
            <div className="about-container cart-main">
                <div className="row justify-content-space-between">
                    <div className="col-lg-8">
                        <h1>Your Cart</h1>
                        {!im_custom.is_logged_in && (<AllreadyHaveAccountHTML />)}

                        {/* Standard Plan */}
                        {userCart.activePlan && (
                            <>
                                <div className="standard">
                                    <div>
                                        <h3>{userCart.activePlan.plan_label}</h3>
                                        <p>{userCart.activePlan.atlas_users} Atlas
                                            memberships{userCart.activePlan.training_tickets > 0 && `, ${userCart.activePlan.training_tickets} Microsoft Licensing Training`}</p>
                                    </div>
                                    <div className="price">
                                        <a
                                            href="#"
                                            className="custom-secondary-btn text-decoration-none"
                                            onClick={() =>
                                                setModalData({
                                                    title: "Remove this plan?",
                                                    message:
                                                        "All users will be removed. Are you sure you want to remove this plan from your cart?",
                                                    action: handleRemoveCart,
                                                }) || setShowModal(true)
                                            }
                                        >
                                            Remove Plan
                                        </a>
                                        <div>{userCart.activePlan ? `${formatPrice(userCart.activePlan.price)}` : ""}</div>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Research 1 — Atlas Membership */}

                        {userCart.activePlan && (
                            <div className="research">
                                <div className="row">
                                    <div className="col-2">
                                        <div className="image-wrap">
                                            <img src="/wp-content/themes/dom/assets/img/atlas-laptop.webp"
                                                alt="Atlas Membership" />
                                        </div>
                                    </div>
                                    <div className="col-10">
                                        <div className="research-one">
                                            <div>
                                                <h3>Atlas Membership</h3>
                                                <p>
                                                    Online 12 Month Subscription access to unbiased analyses, roadmaps, decision kits, infographics, reference guides and more. Atlas simplifies Microsoft for your enterprise. Available 24/7 via desktop, tablet and mobile.
                                                </p>
                                            </div>
                                            {/*<div className="research-text">research</div>*/}
                                        </div>
                                        {/* USERS */}
                                        <PersonList
                                            people={userCart.users}
                                            addPerson={addUser}
                                            removePerson={removeUser}
                                            updatePerson={updateUser}
                                            options={{
                                                extraPersonPrice: userCart.activePlan.extra_atlas_user_price,
                                                typeLabel: "User",
                                                type: 'users'
                                            }} />

                                        <label style={{ lineHeight: "30px" }} className={`position-relative ps-4 ${userCart.highlightUserAgreed ? "shake-text" : ""}`} >
                                            <input
                                                type="checkbox"
                                                ref={userAgreedRef}
                                                className="filter-checkbox position-absolute"
                                                style={{ left: 0 }}
                                                checked={userCart.isUserAgreed == "1" ? true : false}
                                                onChange={(e) => {
                                                    setUserCart((p) => ({
                                                        ...p,
                                                        isUserAgreed: e.target.checked ? "1" : "0"
                                                    }));
                                                }}
                                            />
                                            I agree for all members to receive emails from Directions on Microsoft.
                                        </label>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Research 2 — Microsoft Licensing Training */}
                        {userCart.attendees.length > 0 && (
                            <>
                                {(!userCart.activePlan || userCart.activePlan.training_tickets == 0) && (
                                    <div className="standard">
                                        <div>
                                            <h3>Event</h3>
                                            <p>Microsoft Licensing Training</p>
                                        </div>

                                        <div className="price">
                                            <a
                                                href="#"
                                                className="custom-secondary-btn text-decoration-none"
                                                onClick={(e) =>
                                                    setModalData({
                                                        title: "Remove this event?",
                                                        message:
                                                            "All attendees will be removed. Are you sure you want to remove this event from your cart?",
                                                        action: () => handleRemoveEvent(e),
                                                    }) || setShowModal(true)
                                                }
                                            >
                                                Remove Event
                                            </a>
                                            <div>{im_custom.event_price ? `${formatPrice(im_custom.event_price.price)}` : ""}</div>
                                        </div>
                                    </div>
                                )}
                                <div className="research">
                                    <div className="row">
                                        <div className="col-2">
                                            <div className="image-wrap">
                                                <img src="/wp-content/themes/dom/assets/img/LBC-ticket-thumbnail.png"
                                                    alt="Microsoft Licensing Training" />
                                            </div>
                                        </div>
                                        <div className="col-10">
                                            <div className="research-one">
                                                <div>
                                                    <h3>Microsoft Licensing Training</h3>
                                                    <p>
                                                        18+ hour intensive training and professional certification is the most current and complete training on Microsoft licensing available.  Learn a disciplined process and proven negotiation strategies for optimizing your next contract negotiation.
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="analyst-reports-filter" style={{ position: "relative" }}>
                                                <select className="form-control border-select-control lbc-drop-down" aria-label="Select a Course" id="courseDropdown"
                                                    value={userCart.activeEvent?.event_id || ""}
                                                    onChange={(e) => {
                                                        const selectedEvent = events.find(event => event.event_id.toString() === e.target.value);
                                                        changeActiveEvent(selectedEvent || null);
                                                    }}
                                                >
                                                    <option value="">Select a Course or Choose Later</option>

                                                    {events.length > 0 ? (
                                                        events.map((event) => {
                                                            // Format date range like "NovF 10-12, 2025"
                                                            const start = event.start_date ? new Date(event.start_date) : null;
                                                            const end = event.end_date ? new Date(event.end_date) : null;

                                                            const formattedDate = start
                                                                ? `${start.toLocaleString("en-US", {
                                                                    month: "short",
                                                                    day: "numeric",
                                                                })}-${end ? `${end.getDate()}, ${end.getFullYear()}` : start.getFullYear()}`
                                                                : "";

                                                            // If no city, show Virtual
                                                            const locationText = event.city ? event.city : "Virtual";

                                                            return (
                                                                <option
                                                                    key={event.event_id}
                                                                    value={event.event_id}
                                                                >
                                                                    {`${formattedDate}: ${locationText}`}
                                                                </option>
                                                            );
                                                        })
                                                    ) : (
                                                        <option disabled>No active courses</option>
                                                    )}
                                                </select>
                                            </div>

                                            {/* ATTENDEES */}
                                            <PersonList
                                                people={userCart.attendees}
                                                addPerson={addAttendee}
                                                removePerson={removeAttendee}
                                                updatePerson={updateAttendee}
                                                options={{
                                                    extraPersonPrice: im_custom.event_price.extra_training_tickets_price,
                                                    typeLabel: "Attendee",
                                                    type: 'attendees'
                                                }} />
                                            <label style={{ lineHeight: "30px" }} className={`position-relative ps-4 ${userCart.highlightAttendeeAgreed ? "shake-text" : ""}`} >
                                                <input
                                                    type="checkbox"
                                                    ref={attendeeAgreedRef}
                                                    className="filter-checkbox position-absolute"
                                                    style={{ left: 0 }}
                                                    checked={userCart.isAttendeeAgreed == "1" ? true : false}
                                                    onChange={(e) => {
                                                        setUserCart((p) => ({
                                                            ...p,
                                                            isAttendeeAgreed: e.target.checked ? "1" : "0"
                                                        }));
                                                    }}
                                                />
                                                I agree for all attendees for this training session to receive emails from Directions on Microsoft.
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                        {/*  Show Microsoft Training section only when there are NO attendees in the list */}
                        {userCart.attendees.length === 0 && (<CartFeaturedEventItem im_custom={im_custom} addToCart={addEventToCart} />)}
                        {/*  Show Microsoft Training section only when there are NO plans in the list */}
                        {!userCart.activePlan && (<CartFeaturedPlanItem im_custom={im_custom} />)}
                    </div>

                    {/*  Dynamic Order Summary */}
                    <div className="col-xxl-3 col-lg-4">
                        <OrderSummary userCart={userCart} im_custom={im_custom} grandTotal={grandTotal} continueButtonText={'Continue to Checkout'} continueButtonHandle={handleContinueToCheckout} estimatedTax={0} />
                        <div className="order-summary mt-3 text-center">
                            <h3>Have questions?</h3>
                            <a href="/form/?form_id=18&campaign=Cart_Contact_Us" className="custom-button w-100">
                                Contact Us
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            {/*  Remove Modal */}
            {showModal && (
                <ConfirmPopup
                    title={modalData.title}
                    message={modalData.message}
                    onConfirm={modalData.action}
                    onCancel={() => setShowModal(false)}
                />
            )}
        </div>
    );
};

export default Cart;
